import React from 'react';
import uuid from 'uuid/v4';
import { pick, omit, includes, trim, isEmpty } from 'lodash';
import moment from 'moment';

import DateTime from 'react-datetime';
import NumberInput from './number-input';
import EmailsInput from './emails-input';
import Dropdown from './dropdown';
import AddressInput from './address-input';
import MultiSelectField from './multi-select-input';

const ALLOWED_CONFIG = [
  'type',
  'min',
  'max',
  'step',
  'placeholder',
  'rows',
  'cols',
  'children',
  'disabled',
  'maxLength',
  'insideHint',
  'tabindex',
  'onChange',
  'checked',
  'value',
  'options',
];

const ADDITIONAL_CONFIG = [
  'defaultValue'
];

const EMAILS_CONFIG = [
  'onInvalid',
  'label',
]

export default (config) => {
  const { input, noGroup, label, description,
    subtext, required, meta: { touched, error } } = config;
  const cssClass = touched && error ? 'has-danger' : '';
  const className = buildClassName(config);
  const fieldProps = { className, ...input, ...pick(config, ALLOWED_CONFIG) };
  const additionalProps = { ...pick(config, ADDITIONAL_CONFIG) };

  const additionalEmailsProps = { ...pick(config, EMAILS_CONFIG) };

  if (!fieldProps.id) {
    fieldProps.id = `${fieldProps.name}-${uuid()}`;
  }

  if (fieldProps.type === 'checkbox') return checkboxInput(label, subtext, fieldProps);

  if (fieldProps.type === 'emails') {
    const emailsProps = { ...fieldProps, ...additionalEmailsProps }
    return simpleInput(cssClass, emailsProps);
  }

  if (fieldProps.type === 'radio') return radioButtonInput(label, subtext, fieldProps);

  if (noGroup) return simpleInput(cssClass, fieldProps);

  return (
    <div className={`form-group ${cssClass} pbg-field-${fieldProps.type}`}>
      {renderLabel(label, required, fieldProps.id)}
      {renderDescription(description)}
      {fieldTag(fieldProps, additionalProps)}
      {renderSubtext(subtext, touched && error)}
      {config.keepSubtext && touched && error && renderSubtext(subtext, false)}
    </div>
  );
};

const buildClassName = (config) => {
  const nonFormControl = ['dropdown', 'address', 'multi_select'];
  return [
    includes(nonFormControl, config.type) ? null : 'form-control', config.className
  ].join(' ');
}

const simpleInput = (cssClass, props) => (
  <div className={cssClass}>{fieldTag(props)}</div>
);

const checkboxInput = (label, subtext, props) => (
  <div className="form-check">
    <label className="form-check-label pb-0">
      {fieldTag({ ...props, className: 'form-check-input' })}
      {' '}
      <span>{label}</span>
    </label>
    {subtext &&
      <p>
        {renderSubtext(subtext)}
      </p>
    }
  </div>
);

const radioButtonInput = (label, subtext, props) => (
  <div className="form-group pbg-field-radio clearfix">
    <div className="pbg-field-radio-input-container">
      {fieldTag({ ...props, className: 'form-radio-input' })}
    </div>
    <div className="pbg-field-radio-label-container">
      <label className="pb-0">{label}</label>
      {subtext &&
        <p>
          {renderSubtext(subtext)}
        </p>
      }
    </div>
  </div>
);

export const renderDescription = (description) => {
  if (!description) return null;
  return (
    <div className="mb-2">
      <span className="small-print">{description}</span>
    </div>
  );
};

export const renderSubtext = (subtext, error = false) => {
  if (!subtext && (!error || isEmpty(trim(error)))) return null;
  return (
    <span className={`subtext ${error ? 'error mb-1' : ''}`}>
      <sub className="small-print">{error || subtext}</sub>
    </span>
  );
};

const attachInsideHint = (field, insideHint) => {
  if (insideHint) {
    return (
      <div className="field-with-inside-hint">
        {field}
        <span className="hint">{insideHint}</span>
      </div>
    );
  }
  return field;
};

const renderDatetime = (props) => {
  const inputProps = pick(props, [
    'className',
    'name',
    'placeholder',
    'disabled',
  ]);
  const value = props.value ? moment(props.value).format('DD MMM YYYY hh:mm a') : null;
  return (
    <DateTime
      inputProps={inputProps}
      value={value}
      strictParsing
      dateFormat="DD MMM YYYY"
      timeFormat="hh:mm a"
      onChange={param => props.onChange(param)}
    />
  );
}

const fieldTag = (props, additionalProps = {}) => {
  const fieldProps = omit(props, ['insideHint', 'options']);

  if (props.type === 'textarea') {
    return attachInsideHint(<textarea {...fieldProps} />, props.insideHint);
  }
  if (props.type === 'select') {
    return attachInsideHint(<select {...fieldProps}>{props.children}</select>, props.insideHint);
  }
  if (props.type === 'dropdown') {
    const dropdownProps = { ...fieldProps, ...additionalProps, options: props.options };
    return attachInsideHint(<Dropdown {...dropdownProps} />, props.insideHint);
  }
  if (props.type === 'number') {
    return attachInsideHint(<NumberInput {...fieldProps} />, props.insideHint);
  }
  if (props.type === 'emails') {
    return attachInsideHint(<EmailsInput {...fieldProps} />, props.insideHint);
  }
  if (props.type === 'date' || props.type === 'datetime') {
    return renderDatetime(props);
  }
  if (props.type === 'address') {
    return attachInsideHint(<AddressInput {...fieldProps} />, props.insideHint);
  }
  if (props.type === 'multi_select') {
    const multiSelectProps = { ...fieldProps, options: props.options };
    return attachInsideHint(<MultiSelectField {...multiSelectProps} />, props.insideHint);
  }

  return attachInsideHint(<input {...fieldProps} />, props.insideHint);
};

const markAsRequired = () => <span title="required" className="required-mark">*</span>;

export const renderLabel = (text, required, id) => {
  if (!text) return null;
  return <label htmlFor={id}>{text}{required && markAsRequired()}</label>;
};



// WEBPACK FOOTER //
// ./src/desktop/components/form-field.js